// Module-level cache for /data/colleges.json so multiple components don't
// each fetch the 6,000-row bundle. The fetch is kicked off lazily on first
// access and components subscribe via the useColleges hook.

import { useEffect, useState } from 'react';

let _cache = null;
let _inflight = null;
const _subscribers = new Set();

function _notify() {
  _subscribers.forEach((fn) => fn(_cache));
}

function _load() {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = fetch('/data/colleges.json')
    .then((r) => r.json())
    .then((data) => {
      _cache = data;
      _inflight = null;
      _notify();
      return data;
    })
    .catch((e) => {
      _inflight = null;
      console.error('Error loading colleges.json:', e);
      return [];
    });
  return _inflight;
}

export function useColleges() {
  const [data, setData] = useState(_cache);
  useEffect(() => {
    if (_cache) {
      setData(_cache);
      return;
    }
    const sub = (d) => setData(d);
    _subscribers.add(sub);
    _load();
    return () => _subscribers.delete(sub);
  }, []);
  return data;
}

/// Reduce IPEDS `inst_url` (which can be `https://www.fsu.edu`,
/// `www.akbible.edu`, or `https://example.edu/admissions/`) to the bare
/// hostname Google s2 favicons expects.
export function domainForFavicon(instUrl) {
  if (!instUrl) return null;
  let s = instUrl.trim().toLowerCase();
  const protoIdx = s.indexOf('://');
  if (protoIdx >= 0) s = s.slice(protoIdx + 3);
  if (s.startsWith('www.')) s = s.slice(4);
  const slash = s.indexOf('/');
  if (slash >= 0) s = s.slice(0, slash);
  return s || null;
}

/// Look up an IPEDS row by unitid from a pre-fetched colleges array.
export function findCollegeByUnitid(colleges, unitid) {
  if (!colleges || !unitid) return null;
  return colleges.find((c) => c.u === unitid) || null;
}

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
  KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
  MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico',
};
// Words a person types that no college name needs matched: "Miami of Ohio".
const FILLER = new Set(['of', 'the', 'at', 'and', 'in']);

// Apostrophes are dropped, not split on: "St. John's" must be "st johns".
const words = (s) => (s || '').toLowerCase().replace(/['\u2019]/g, '').split(/[^a-z0-9]+/).filter(Boolean);
// "St" and "Saint" are the same word in college names, either way round.
const wordMatches = (w, t) => w.startsWith(t) || (t === 'st' && w === 'saint') || (t === 'saint' && w === 'st');

const _prepared = new WeakMap();
function _prepare(list) {
  let p = _prepared.get(list);
  if (!p) {
    p = list.map((c, i) => {
      const nameWords = words(c.n);
      return {
        c, i, nameWords,
        nameNorm: nameWords.join(' '),
        allWords: [...nameWords, ...words(c.c), ...words(c.s), ...words(STATE_NAMES[c.s])],
        raw: `${(c.n || '').toLowerCase()}\n${(c.c || '').toLowerCase()}`,
      };
    });
    _prepared.set(list, p);
  }
  return p;
}

/// Ranked college search over colleges.json rows ({n, c, s, d}).
///
/// The old filter was a raw substring of name or city, in file order, first 15:
/// "kansas state" returned seven ArKANSAS State campuses before Kansas State,
/// and "miami of ohio" matched nothing because the state was never searched
/// (the row is "Miami University-Oxford", OH). Now every query word must start
/// a word of the name, city or state (code or full name), ranked:
///   0 name starts with the query · 1 all words in the name ·
///   2 needs the city/state · 3 old raw-substring match (kept so nothing
///   findable before stops being findable).
/// Ties go to more query words in the name, then a known athletic division,
/// then file order.
export function searchColleges(list, query, limit = 15) {
  const all = words(query);
  const tokens = all.filter((t) => !FILLER.has(t));
  if (!list || !list.length || !all.length) return [];
  const q = (tokens.length ? tokens : all);
  const qNorm = q.join(' ');
  const raw = query.trim().toLowerCase();
  const prefixOf = (ws) => (t) => ws.some((w) => wordMatches(w, t));
  const ranked = [];
  for (const r of _prepare(list)) {
    let tier;
    if (r.nameNorm.startsWith(qNorm)) tier = 0;
    else if (q.every(prefixOf(r.nameWords))) tier = 1;
    else if (q.every(prefixOf(r.allWords))) tier = 2;
    else if (r.raw.includes(raw)) tier = 3;
    else continue;
    // Within a tier, more of the query in the NAME wins: "miami ohio" must
    // rank Miami University (OH) above a school that only sits in Miamisburg.
    const inName = q.filter(prefixOf(r.nameWords)).length;
    ranked.push({ r, tier, inName });
  }
  ranked.sort((a, b) => a.tier - b.tier || b.inName - a.inName
    || (b.r.c.d ? 1 : 0) - (a.r.c.d ? 1 : 0) || a.r.i - b.r.i);
  return ranked.slice(0, limit).map((x) => x.r.c);
}
